import {ScaleLinear} from 'd3';
import * as d3 from 'd3';

import {LayeringMode, AllPolymerLayouts, PolymerLayout} from 'constants/MapsConstants';
import BaseReactWrapperView, {BaseReactWrapperViewOptions} from 'molbio/maps/linear/BaseReactWrapperView';
import {LayersDict} from 'molbio/maps/linear/LayeringHelpers';

export interface LayeredReactWrapperViewOptions<
  TLayeringMode extends LayeringMode,
  TModel,
  LayoutName extends keyof AllPolymerLayouts,
> extends BaseReactWrapperViewOptions<LayoutName> {
  layeringMode: TLayeringMode;
  singleHeight: () => number;
  getLayersDict: (sliceModels: Array<TModel>, layeringMode: TLayeringMode) => LayersDict;
  getSliceModels: () => Array<TModel>;
  renderReact: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: () => void,
    sliceModels: Array<TModel>,
    layersDict: LayersDict,
    layeringMode: LayeringMode,
    yFn: ScaleLinear<number, number>
  ) => JSX.Element;
}

/**
 * A ReactWrapperView extension for rendering layered / overlapping views such as primers and parts.
 *
 * boxHeight and _staticBoxHeight are ignored in this class; instead, we calculate box height as a multiple of
 * a single feature height.
 *
 * @param options.layeringMode How to layer features. Most views should use MapsConstants.LINEAR_LAYER_FULL.
 *   Annotations use MapsConstants.LINEAR_LAYER_INLINE in order to show the name of annotations regardless of
 *   their length.
 * @param options.singleHeight A function to get the height of a single layer for this view, including any
 *   margins.
 * @param options.getLayersDict A function to get a map of model index within sliceModels to the index of the
 *   layer it should be on.
 * @param options.getSliceModels A function to determine a list of RegionSelectable relevant to this slice,
 *   given the SliceViewModel
 */
export default class LayeredReactWrapperView<
  TLayeringMode extends LayeringMode,
  TModel,
  LayoutName extends keyof AllPolymerLayouts,
> extends BaseReactWrapperView<LayoutName> {
  layeringMode!: TLayeringMode;
  singleHeight!: () => number;
  getLayersDict!: (sliceModels: Array<TModel>, layeringMode: TLayeringMode) => LayersDict;
  getSliceModels!: () => Array<TModel>;
  renderReact!: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: () => void,
    sliceModels: Array<TModel>,
    layersDict: LayersDict,
    layeringMode: TLayeringMode,
    yFn: ScaleLinear<number, number>
  ) => JSX.Element;

  constructor(options: LayeredReactWrapperViewOptions<TLayeringMode, TModel, LayoutName>) {
    super(options);
  }

  override initialize(options: LayeredReactWrapperViewOptions<TLayeringMode, TModel, LayoutName>): void {
    super.initialize(options);
    this.layeringMode = options.layeringMode;
    this.singleHeight = options.singleHeight;
    this.getLayersDict = options.getLayersDict;
    this.getSliceModels = options.getSliceModels;
    this.renderReact = options.renderReact;
  }

  /**
   * Calculates how many horizontal layers this slice needs.
   */
  numLayers(): number {
    return Object.keys(this.getLayersDict(this.getSliceModels(), this.layeringMode)).length;
  }

  /**
   * Makes a d3 ScaleLinear that calculates the y position of a feature, given its y-index.
   */
  getYFn(): ScaleLinear<number, number> {
    const overlaps = this.numLayers();
    return d3
      .scaleLinear()
      .domain([0, overlaps])
      .range([0, overlaps * (this.singleHeight() + this.getSingleVMargin())]);
  }

  override boxHeight(): number {
    const numLayers = this.numLayers();
    if (!this.shouldShow() || numLayers === 0) {
      return 0;
    } else {
      return numLayers * (this.singleHeight() + this.getSingleVMargin()) + this.getLayout().vMargin;
    }
  }

  getSingleVMargin(): number {
    return (this.getLayout() as PolymerLayout).vMarginSingle ?? 0;
  }

  redraw(): void {
    this.trigger('redraw');
  }

  _renderComponentWrapper(): JSX.Element {
    return this.renderReact(
      this.getLayout(),
      this.redraw.bind(this),
      this.getSliceModels(),
      this.getLayersDict(this.getSliceModels(), this.layeringMode),
      this.layeringMode,
      this.getYFn()
    );
  }
}
